/**
 * This module handles the DOM updates in the mini-cart
 * Actions and state are managed by CartObserver
 * We re-render the whole mini-cart section after each cart event.
 * As such we need to re-init our event listeners after re-render. Much of this code below handles that.
 */

import '../../styles/sections/mini-cart.css';
import Cart from 'UTILS/cart/CartObserver';
import { getSectionDOM, getParentShopifySection } from 'UTILS/DOM/shopifySectionRender';
import {
  getFocusTrap,
  setScrollBarWidth,
  handleMenuOpened,
  handleMenuClosed,
  addEscapeKeyListener,
} from 'UTILS/drawer-menu-handler';
import logger from '../utilities/logger';
// add the carousel functionality in here
import initMiniCartCarousel from './mini-cart-carousel';

const cart = new Cart();

/* DOM elements: we reset after re-render, hence 'let' */
const cartIconMobile = document.querySelector('[data-cart-drawer-trigger=mobile]');
const cartIconDesktop = document.querySelector('[data-cart-drawer-trigger=desktop]');
const cartBackdrop = document.querySelector('[data-id=mini-cart-backdrop]');

const miniCart = document.querySelector('.MiniCart');
const miniCartTrap = getFocusTrap(miniCart);

let miniCartClose = document.querySelector('[data-id=mini-cart-close]');
let loaders = miniCart && miniCart.querySelectorAll('.load');
let removeBtns = document.querySelectorAll('[data-id=mini-cart-remove-item]');
let quantitySelects = document.querySelectorAll('[data-id=mini-cart-quantity-select]');
let cartError = document.querySelector('[data-id=cart-error]');

addEscapeKeyListener(miniCartTrap, miniCart, cartBackdrop);
setScrollBarWidth();

const handleCartOpened = () => {
  handleMenuOpened(miniCartTrap, miniCart, cartBackdrop);
};

const handleCartClosed = () => {
  handleMenuClosed(miniCartTrap, miniCart, cartBackdrop);
};

function initMiniCartDOM() {
  loaders = miniCart.querySelectorAll('.load');
  miniCartClose = document.querySelector('[data-id=mini-cart-close]');
  removeBtns = document.querySelectorAll('[data-id=mini-cart-remove-item]');
  quantitySelects = document.querySelectorAll('[data-id=mini-cart-quantity-select]');
  cartError = document.querySelector('[data-id=cart-error]');
  miniCartClose.addEventListener('click', handleCartClosed);
}

function initMiniCart() {
  if (cartIconMobile) cartIconMobile.addEventListener('click', handleCartOpened);
  if (cartIconDesktop) cartIconDesktop.addEventListener('click', handleCartOpened);
  if (cartBackdrop) cartBackdrop.addEventListener('click', handleCartClosed);
  initMiniCartDOM();
  initMiniCartCarousel();
}

// Take the new html, re-render in the mini-cart, and re-init the event listeners
function reRenderMiniCart(html) {
  const parent = getParentShopifySection(miniCart);
  const newHtml = getSectionDOM(html);
  const cartContent = parent.querySelector('.MiniCart-renderableContent');
  const newCartContent = newHtml.querySelector('.MiniCart-renderableContent');
  if (cartContent && newCartContent) {
    cartContent.innerHTML = newCartContent.innerHTML;
    initMiniCartDOM();
    initMiniCartCarousel();
  } else {
    // TODO: something went wrong, fallback
  }
}

// users can't perform this action while cart is doing work
function disableRemoveBtns() {
  if (removeBtns) {
    removeBtns.forEach((btn) => {
      const button = btn;
      button.disabled = true;
    });
  }
}

function enableRemoveBtns() {
  if (removeBtns) {
    removeBtns.forEach((btn) => {
      const button = btn;
      button.disabled = false;
    });
  }
}

// displays the skeleton loading for each el
function showLoading() {
  if (loaders) {
    loaders.forEach((el) => {
      el.classList.add('u-skeletonLoader');
    });
  }
}

function removeLoading() {
  if (loaders) {
    loaders.forEach((el) => {
      el.classList.remove('u-skeletonLoader');
    });
  }
}

// while the cart is doing work, we show the loading and ensure the mini-cart is open
function renderCartBeforeUpdate() {
  showLoading();
  if (!miniCart.classList.contains('active')) {
    handleCartOpened();
  }
}

// takes a function that is the cb for the event listener for the 'remove' action.
function handleRemoveCartItem(event) {
  return cart.removeFromCartById(event.target.dataset.lineItemId);
}

// takes a function that is the cb for the event listener for the 'update quantity' action.
function handleQuantitySelection(event) {
  event.target.blur();
  cart.changeQuantityById(event.target.dataset.lineItemId, event.target.value);
}

// init remove button event listeners. fn arg comes from observer
function initRemoveBtns() {
  removeBtns.forEach((btn) => {
    btn.addEventListener('click', handleRemoveCartItem);
  });
}

// init quantity select event listeners. fn arg comes from observer
function initQuantitySelect() {
  quantitySelects.forEach((select) => {
    select.addEventListener('change', handleQuantitySelection);
  });
}

async function renderCartAfterUpdate() {
  const html = await fetch(`${window.location.pathname}?section_id=mini-cart`)
    .then((res) => res.text())
    .catch((e) => {
      logger.error('renderCartAfterUpdate', e);
    });
  // need to reinit new html
  reRenderMiniCart(html);
  initRemoveBtns();
  initQuantitySelect();
}

function displayError(error) {
  if (!cartError) return;

  if (error instanceof Error && error.message) {
    cartError.textContent = error.message;
  }
}

function removeError() {
  if (!cartError) return;
  cartError.textContent = '';
}

cart.subscribe('beforeLoad', initMiniCart); // init cart related evt listeners
cart.subscribe('beforeLoad', initRemoveBtns); // init cart related evt listeners
cart.subscribe('beforeLoad', initQuantitySelect); // init cart related evt listeners

cart.subscribe('beforeUpdate', renderCartBeforeUpdate);
cart.subscribe('beforeUpdate', disableRemoveBtns);
cart.subscribe('afterUpdate', renderCartAfterUpdate); // re-init cart related evt listeners. Pass handlers.
cart.subscribe('error', removeLoading);
cart.subscribe('error', enableRemoveBtns);
cart.subscribe('error', displayError);
cart.subscribe('beforeUpdate', removeError);
